import type { FlowNode, FlowEdge, WidgetDefinition } from './types';
import { widgetOutputs, widgetRegistry } from './registry';

export type VariableInfo = {
  name: string;
  description: string;
  type: 'string';
  sourceNodeId: string;
  sourceWidgetName: string;
  isSystem?: boolean;
};

export const systemVariables: VariableInfo[] = [
  {
    name: 'callerId',
    description: 'Phone number of the incoming caller',
    type: 'string',
    sourceNodeId: 'system',
    sourceWidgetName: 'System',
    isSystem: true,
  },
  {
    name: 'calledNumber',
    description: 'Phone number that received the call',
    type: 'string',
    sourceNodeId: 'system',
    sourceWidgetName: 'System',
    isSystem: true,
  },
  {
    name: 'callSessionId',
    description: 'Unique identifier for this call session',
    type: 'string',
    sourceNodeId: 'system',
    sourceWidgetName: 'System',
    isSystem: true,
  },
  {
    name: 'callStartDate',
    description: 'Date when the call started (YYYY-MM-DD)',
    type: 'string',
    sourceNodeId: 'system',
    sourceWidgetName: 'System',
    isSystem: true,
  },
  {
    name: 'callStartTime',
    description: 'Time when the call started (HH:MM:SS)',
    type: 'string',
    sourceNodeId: 'system',
    sourceWidgetName: 'System',
    isSystem: true,
  },
  {
    name: 'callDuration',
    description: 'Current duration of the call in seconds',
    type: 'string',
    sourceNodeId: 'system',
    sourceWidgetName: 'System',
    isSystem: true,
  },
];

function getHandleType(node: FlowNode, handleId: string): string | undefined {
  return widgetOutputs(node.type, node.data.config as Record<string, unknown>)?.find((h) => h.id === handleId)?.type;
}

function getUpstreamNodes(nodes: FlowNode[], edges: FlowEdge[], currentNodeId: string): Map<string, Set<string>> {
  const result = new Map<string, Set<string>>();
  const visited = new Set<string>();

  function traverse(nodeId: string) {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);

    for (const edge of edges) {
      if (edge.target !== nodeId) continue;

      const sourceNode = nodes.find((n) => n.id === edge.source);
      if (!sourceNode) continue;

      const handleType = edge.sourceHandle ? getHandleType(sourceNode, edge.sourceHandle) : undefined;
      const existing = result.get(edge.source);
      if (existing) {
        if (handleType) existing.add(handleType);
      } else {
        const types = new Set<string>();
        if (handleType) types.add(handleType);
        result.set(edge.source, types);
      }

      traverse(edge.source);
    }
  }

  traverse(currentNodeId);
  return result;
}

export type CrmVariableMapping = { variableName: string; crmField: string };

export function getAvailableVariables(
  nodes: FlowNode[],
  edges: FlowEdge[],
  currentNodeId: string,
  crmVariables?: CrmVariableMapping[],
): VariableInfo[] {
  const upstreamNodes = getUpstreamNodes(nodes, edges, currentNodeId);
  const variables: VariableInfo[] = [...systemVariables];

  if (crmVariables && crmVariables.length > 0) {
    const crmSourceNodes: { nodeId: string; widgetName: string }[] = [];

    const startNode = nodes.find((n) => n.type === 'start');
    if (startNode) {
      const startConfig = startNode.data.config as { enableCallerIdMatch?: boolean };
      if (startConfig?.enableCallerIdMatch && upstreamNodes.get(startNode.id)?.has('success')) {
        crmSourceNodes.push({ nodeId: startNode.id, widgetName: 'Start' });
      }
    }

    for (const node of nodes) {
      if (node.type !== 'getCustomerId') continue;
      if (upstreamNodes.get(node.id)?.has('success')) {
        crmSourceNodes.push({ nodeId: node.id, widgetName: 'Get Customer ID' });
      }
    }

    for (const source of crmSourceNodes) {
      for (const mapping of crmVariables) {
        variables.push({
          name: mapping.variableName,
          description: `CRM field: ${mapping.crmField}`,
          type: 'string',
          sourceNodeId: source.nodeId,
          sourceWidgetName: source.widgetName,
        });
      }
    }
  }

  for (const [nodeId, viaHandleTypes] of upstreamNodes) {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) continue;

    const widget = widgetRegistry.get(node.type) as WidgetDefinition | undefined;
    if (!widget?.variables?.outputs) continue;

    for (const output of widget.variables.outputs) {
      if (output.availableOnHandleTypes && viaHandleTypes.size > 0) {
        const available = output.availableOnHandleTypes.some((t) => viaHandleTypes.has(t));
        if (!available) continue;
      }

      const variableName =
        'name' in output
          ? output.name
          : ((node.data.config as Record<string, unknown>)[output.configKey] as string | undefined);

      if (typeof variableName === 'string' && variableName.trim()) {
        variables.push({
          name: variableName,
          description: output.description,
          type: output.type,
          sourceNodeId: nodeId,
          sourceWidgetName: widget.name,
        });
      }
    }
  }

  return variables;
}

export function interpolateVariables(template: string, variables: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
    return variables[varName] ?? match;
  });
}
